import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatCurrency } from '../utils/currency';
import { getTranslation } from '../utils/translations';

const Orders = () => {
  const { t } = useTranslation();
  const { token, API } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editOrderDialogOpen, setEditOrderDialogOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [orderType, setOrderType] = useState('dine-in');
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentData, setPaymentData] = useState({ method: 'cash', amount: 0 });

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders`);
      setOrders(res.data);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const fetchMenuItems = async () => {
    try {
      const res = await axios.get(`${API}/menu-items`);
      setMenuItems(res.data);
    } catch (error) {
      console.error('Error fetching menu items');
    }
  };

  const fetchTables = async () => {
    try {
      const res = await axios.get(`${API}/tables`);
      setTables(res.data);
    } catch (error) {
      console.error('Error fetching tables');
    }
  };

  const handleAddItem = (item) => {
    const existing = selectedItems.find(i => i.menu_item_id === item.id);
    if (existing) {
      setSelectedItems(selectedItems.map(i =>
        i.menu_item_id === item.id ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setSelectedItems([...selectedItems, {
        menu_item_id: item.id,
        quantity: 1,
        price: item.base_price,
        modifiers: []
      }]);
    }
  };

  const handleRemoveItem = (menuItemId) => {
    setSelectedItems(selectedItems.filter(i => i.menu_item_id !== menuItemId));
  };

  const handleIncreaseQuantity = (menuItemId) => {
    setSelectedItems(selectedItems.map(i =>
      i.menu_item_id === menuItemId ? { ...i, quantity: i.quantity + 1 } : i
    ));
  };

  const handleDecreaseQuantity = (menuItemId) => {
    setSelectedItems(selectedItems.map(i =>
      i.menu_item_id === menuItemId 
        ? { ...i, quantity: Math.max(1, i.quantity - 1) } 
        : i
    ));
  };

  const handleCreateOrder = async () => {
    if (selectedItems.length === 0) {
      toast.error(t('messages.addItemsToOrder'));
      return;
    }

    try {
      await axios.post(`${API}/orders`, {
        table_id: orderType === 'dine-in' ? selectedTable : null,
        order_type: orderType,
        items: selectedItems,
        discount: 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('success'));
      setDialogOpen(false);
      setSelectedItems([]);
      setSelectedTable('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('success'));
      fetchOrders();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const openPaymentDialog = (order) => {
    setSelectedOrder(order);
    setPaymentData({ method: 'cash', amount: order.total });
    setPaymentDialogOpen(true);
  };

  const handleProcessPayment = async () => {
    try {
      await axios.post(`${API}/orders/${selectedOrder.id}/payment`, paymentData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('messages.paymentProcessed'));
      setPaymentDialogOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const openEditOrderDialog = async (order) => {
    setSelectedOrder(order);
    // Load existing order items
    setSelectedItems(order.items.map(item => ({
      menu_item_id: item.menu_item_id,
      quantity: item.quantity,
      price: item.price,
      modifiers: item.modifiers || []
    })));
    setEditOrderDialogOpen(true);
  };

  const handleSaveOrderChanges = async () => {
    if (selectedItems.length === 0) {
      toast.error(t('messages.orderMustHaveItems'));
      return;
    }

    try {
      await axios.put(`${API}/orders/${selectedOrder.id}/items`, {
        items: selectedItems,
        discount: selectedOrder.discount || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('messages.orderUpdated'));
      setEditOrderDialogOpen(false);
      setSelectedItems([]);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      preparing: 'badge-info',
      ready: 'badge-success',
      completed: 'badge-secondary',
      cancelled: 'badge-danger'
    };
    return badges[status] || 'badge-secondary';
  };

  useEffect(() => {
    fetchOrders();
    fetchMenuItems();
    fetchTables();
  }, []);

  const subtotal = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <div className="orders">
      <div className="section-header">
        <h2 className="section-title">{t('orders.orders')}</h2>
        <Button onClick={() => setDialogOpen(true)} data-testid="new-order-btn">
          <Plus size={16} /> {t('orders.newOrder')}
        </Button>
      </div>

      <Tabs defaultValue="all" className="w-full mt-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="all-orders-tab">All</TabsTrigger>
          <TabsTrigger value="active" data-testid="active-orders-tab">Active</TabsTrigger>
          <TabsTrigger value="completed" data-testid="completed-orders-tab">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('orders.orderType')}</th>
                  <th>{t('orders.subtotal')}</th>
                  <th>VAT</th>
                  <th>{t('orders.total')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('orders.paymentMethod')}</th>
                  <th>Time</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} data-testid={`order-row-${order.id}`}>
                    <td>{order.id.substring(0, 8)}</td>
                    <td>{t(order.order_type)}</td>
                    <td data-testid="order-subtotal">{formatCurrency(order.subtotal)}</td>
                    <td data-testid="order-vat">{formatCurrency(order.vat_amount || 0)}</td>
                    <td data-testid="order-total">{formatCurrency(order.total)}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(order.status)}`} data-testid="order-status">
                        {t(order.status)}
                      </span>
                    </td>
                    <td>{order.payment_method || '-'}</td>
                    <td className="text-sm">
                      {new Date(order.created_at).toLocaleString()}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        {order.status !== 'completed' && order.status !== 'cancelled' && (
                          <>
                            <button
                              onClick={() => openEditOrderDialog(order)}
                              className="action-btn view-btn"
                              data-testid="view-edit-order-btn"
                              title="View/Edit order"
                            >
                              <Eye size={14} />
                            </button>
                            <select
                              value={order.status}
                              onChange={(e) => handleUpdateStatus(order.id, e.target.value)}
                              className="status-select"
                              data-testid="status-select"
                            >
                              <option value="pending">{t('orders.pending')}</option>
                              <option value="preparing">{t('orders.preparing')}</option>
                              <option value="ready">{t('orders.ready')}</option>
                              <option value="completed">{t('orders.completed')}</option>
                            </select>
                            {order.payment_status !== 'paid' && (
                              <button
                                onClick={() => openPaymentDialog(order)}
                                className="action-btn payment-btn"
                                data-testid="payment-btn"
                                title="Process payment"
                              >
                                $
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="active">
          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('orders.orderType')}</th>
                  <th>{t('orders.subtotal')}</th>
                  <th>VAT</th>
                  <th>{t('orders.total')}</th>
                  <th>{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.filter(o => ['pending', 'preparing', 'ready'].includes(o.status)).map((order) => (
                  <tr key={order.id}>
                    <td>{order.id.substring(0, 8)}</td>
                    <td>{t(order.order_type)}</td>
                    <td>{formatCurrency(order.subtotal)}</td>
                    <td>{formatCurrency(order.vat_amount || 0)}</td>
                    <td>{formatCurrency(order.total)}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(order.status)}`}>
                        {t(order.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="completed">
          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('orders.orderType')}</th>
                  <th>{t('orders.subtotal')}</th>
                  <th>VAT</th>
                  <th>{t('orders.total')}</th>
                  <th>{t('orders.paymentMethod')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.filter(o => o.status === 'completed').map((order) => (
                  <tr key={order.id}>
                    <td>{order.id.substring(0, 8)}</td>
                    <td>{t(order.order_type)}</td>
                    <td>{formatCurrency(order.subtotal)}</td>
                    <td>{formatCurrency(order.vat_amount || 0)}</td>
                    <td>{formatCurrency(order.total)}</td>
                    <td>{order.payment_method || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* New Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl" data-testid="order-dialog" aria-describedby="order-dialog-description">
          <DialogHeader>
            <DialogTitle>{t('orders.newOrder')}</DialogTitle>
            <p id="order-dialog-description" className="text-sm text-gray-500">
              Create a new order by selecting menu items and specifying order details.
            </p>
          </DialogHeader>

          <div className="order-form">
            <div className="order-settings">
              <div className="form-group">
                <label>{t('orders.orderType')}</label>
                <select
                  value={orderType}
                  onChange={(e) => setOrderType(e.target.value)}
                  data-testid="order-type-select"
                >
                  <option value="dine-in">{t('orders.dineIn')}</option>
                  <option value="takeaway">{t('orders.takeaway')}</option>
                  <option value="delivery">{t('orders.delivery')}</option>
                </select>
              </div>

              {orderType === 'dine-in' && (
                <div className="form-group">
                  <label>{t('tables.tableNumber')}</label>
                  <select
                    value={selectedTable}
                    onChange={(e) => setSelectedTable(e.target.value)}
                    data-testid="table-select"
                  >
                    <option value="">Select table...</option>
                    {tables.filter(t => t.status === 'available').map(table => (
                      <option key={table.id} value={table.id}>Table {table.number}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="order-content">
              <div className="menu-list">
                <h3 className="mb-3">{t('menu')}</h3>
                <div className="menu-items">
                  {menuItems.map(item => (
                    <div key={item.id} className="menu-item" onClick={() => handleAddItem(item)} data-testid={`menu-item-${item.id}`}>
                      <div className="item-name">{getTranslation(item.translations, 'en', 'name')}</div>
                      <div className="item-price">{formatCurrency(item.base_price)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="order-summary">
                <h3 className="mb-3">Order Items</h3>
                <div className="selected-items">
                  {selectedItems.length === 0 ? (
                    <p className="text-sm text-gray-500">No items selected</p>
                  ) : (
                    selectedItems.map((item, idx) => {
                      const menuItem = menuItems.find(m => m.id === item.menu_item_id);
                      return (
                        <div key={idx} className="selected-item-with-controls" data-testid="selected-item">
                          <div className="item-details">
                            <div className="item-name">{menuItem ? getTranslation(menuItem.translations, 'en', 'name') : ''}</div>
                            <div className="item-price-per">{formatCurrency(item.price)}</div>
                          </div>
                          <div className="item-controls">
                            <div className="quantity-controls">
                              <button 
                                onClick={() => handleDecreaseQuantity(item.menu_item_id)}
                                className="qty-btn"
                                data-testid="decrease-qty-btn"
                              >
                                -
                              </button>
                              <span className="quantity-display">{item.quantity}</span>
                              <button 
                                onClick={() => handleIncreaseQuantity(item.menu_item_id)}
                                className="qty-btn"
                                data-testid="increase-qty-btn"
                              >
                                +
                              </button>
                            </div>
                            <div className="item-total">{formatCurrency(item.price * item.quantity)}</div>
                            <button
                              onClick={() => handleRemoveItem(item.menu_item_id)}
                              className="remove-item-btn"
                              data-testid="remove-item-btn"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="order-total" data-testid="order-subtotal">
                  <strong>{t('total')}:</strong>
                  <strong>{formatCurrency(subtotal)}</strong>
                </div>
                <Button onClick={handleCreateOrder} className="w-full" data-testid="create-order-btn">
                  {t('actions.confirm')}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

 

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent data-testid="payment-dialog" aria-describedby="payment-dialog-description">
          <DialogHeader>
            <DialogTitle>{t('orders.processPayment')}</DialogTitle>
            <p id="payment-dialog-description" className="text-sm text-gray-500">
              Process payment for this order.
            </p>
          </DialogHeader>
          <div className="payment-form">
            {selectedOrder && (
              <div className="order-summary-payment">
                <p>Order Total: <strong>{formatCurrency(selectedOrder.total)}</strong></p>
              </div>
            )}
            <div className="form-group">
              <label>{t('paymentMethod')}</label>
              <select
                value={paymentData.method}
                onChange={(e) => setPaymentData({...paymentData, method: e.target.value})}
                data-testid="payment-method-select"
              >
                <option value="cash">{t('cash')}</option>
                <option value="card">{t('card')}</option>
                <option value="digital">Digital</option>
              </select>
            </div>
            <div className="form-group">
              <label>Amount</label>
              <input
                type="number"
                step="0.01"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value)})}
                data-testid="payment-amount-input"
              />
            </div>
            <Button onClick={handleProcessPayment} className="w-full" data-testid="process-payment-btn">
              {t('processPayment')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={editOrderDialogOpen} onOpenChange={setEditOrderDialogOpen}>
        <DialogContent className="max-w-4xl" data-testid="edit-order-dialog" aria-describedby="edit-order-dialog-description">
          <DialogHeader>
            <DialogTitle>View/Edit Order</DialogTitle>
            <p id="edit-order-dialog-description" className="text-sm text-gray-500">
              View order details and modify items as needed.
            </p>
          </DialogHeader>
          <div className="order-form">
            {selectedOrder && (
              <div className="order-info">
                <div className="order-details">
                  <p><strong>Order ID:</strong> {selectedOrder.id.substring(0, 8)}</p>
                  <p><strong>Type:</strong> {t(selectedOrder.order_type)}</p>
                  <p><strong>Status:</strong> {t(selectedOrder.status)}</p>
                  <p><strong>Total:</strong> {formatCurrency(selectedOrder.total)}</p>
                </div>
              </div>
            )}
            <div className="order-content">
              <div className="menu-list">
                <h3 className="mb-3">{t('menu')}</h3>
                <div className="menu-items">
                  {menuItems.map(item => (
                    <div key={item.id} className="menu-item" onClick={() => handleAddItem(item)} data-testid={`edit-menu-item-${item.id}`}>
                      <div className="item-name">{getTranslation(item.translations, 'en', 'name')}</div>
                      <div className="item-price">{formatCurrency(item.base_price)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="order-summary">
                <h3 className="mb-3">Order Items</h3>
                <div className="selected-items">
                  {selectedItems.length === 0 ? (
                    <p className="text-sm text-gray-500">No items in order</p>
                  ) : (
                    selectedItems.map((item, idx) => {
                      const menuItem = menuItems.find(m => m.id === item.menu_item_id);
                      return (
                        <div key={idx} className="selected-item-with-controls" data-testid="edit-selected-item">
                          <div className="item-details">
                            <div className="item-name">{menuItem ? getTranslation(menuItem.translations, 'en', 'name') : ''}</div>
                            <div className="item-price-per">{formatCurrency(item.price)}</div>
                          </div>
                          <div className="item-controls">
                            <div className="quantity-controls">
                              <button 
                                onClick={() => handleDecreaseQuantity(item.menu_item_id)}
                                className="qty-btn"
                                data-testid="edit-decrease-qty-btn"
                              >
                                -
                              </button>
                              <span className="quantity-display">{item.quantity}</span>
                              <button 
                                onClick={() => handleIncreaseQuantity(item.menu_item_id)}
                                className="qty-btn"
                                data-testid="edit-increase-qty-btn"
                              >
                                +
                              </button>
                            </div>
                            <div className="item-total">{formatCurrency(item.price * item.quantity)}</div>
                            <button
                              onClick={() => handleRemoveItem(item.menu_item_id)}
                              className="remove-item-btn"
                              data-testid="edit-remove-item-btn"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="order-total" data-testid="edit-order-subtotal">
                  <strong>Total:</strong>
                  <strong>{formatCurrency(selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0))}</strong>
                </div>
                <Button onClick={handleSaveOrderChanges} className="w-full" data-testid="save-order-changes-btn">
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        .orders {
          width: 100%;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .section-title {
          font-size: 20px;
          font-weight: 600;
        }

        .status-select {
          padding: 4px 8px;
          font-size: 12px;
          border-radius: 6px;
        }

        .order-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .order-settings {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .order-content {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 20px;
        }

        .menu-list h3,
        .order-summary h3 {
          font-size: 16px;
          font-weight: 600;
        }

        .menu-items {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        .menu-item {
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .menu-item:hover {
          background: #f3f4f6;
        }

        .item-name {
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .item-price {
          font-size: 13px;
          color: #666;
        }

        .selected-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        }

        .selected-item {
          display: flex;
          justify-content: space-between;
          padding: 8px;
          background: #f9fafb;
          border-radius: 6px;
          font-size: 13px;
        }

        .order-info {
          margin-bottom: 20px;
        }

        .order-details {
          display: flex;
          gap: 20px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          font-size: 14px;
        }

        .selected-item-with-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f9fafb;
          border-radius: 6px;
          font-size: 13px;
        }

        .item-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .item-price-per {
          font-size: 12px;
          color: #666;
        }

        .item-controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .quantity-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .qty-btn {
          width: 24px;
          height: 24px;
          border: 1px solid hsl(var(--border));
          background: hsl(var(--card));
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: bold;
          color: hsl(var(--foreground));
        }

        .qty-btn:hover {
          background: hsl(var(--muted));
        }

        .quantity-display {
          min-width: 20px;
          text-align: center;
          font-weight: 500;
        }

        .item-total {
          font-weight: 500;
          min-width: 60px;
          text-align: right;
        }

        .remove-item-btn {
          background: #fee2e2;
          color: #dc2626;
          border: none;
          border-radius: 4px;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 18px;
          line-height: 1;
        }

        .remove-item-btn:hover {
          background: #fecaca;
        }

        .order-total {
          display: flex;
          justify-content: space-between;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 13px;
          font-weight: 600;
          color: #333;
        }

        .flex {
          display: flex;
        }

        .gap-2 {
          gap: 8px;
        }

        .text-sm {
          font-size: 12px;
          color: #666;
        }

        .action-btn {
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .view-btn {
          background: #6b7280;
          color: white;
        }

        .view-btn:hover {
          background: #4b5563;
        }

        .payment-btn {
          background: #3b82f6;
          color: white;
        }

        .payment-btn:hover {
          background: #2563eb;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .payment-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .payment-form input,
        .payment-form select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
        }

        .payment-form input:focus,
        .payment-form select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
      `}</style>
    </div>
  );
};

export default Orders;
